/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React, { useState } from "react";
import {
  BellIcon,
  ClockIcon,
  CloudIcon,
  MapPinIcon,
  PresentationChartLineIcon,
  PhotoIcon,
  MagnifyingGlassPlusIcon,
  LockClosedIcon,
  ShieldCheckIcon,
  LanguageIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { ArrowRightStartOnRectangleIcon as LogoutIcon } from "@heroicons/react/24/outline";
import { NotificationSettings } from "./settings/NotificationSettings";
import { TimeFormatSettings } from "./settings/TimeFormatSettings";
import { DistanceSettings } from "./settings/DistanceSettings";
import { DashboardSettings } from "./settings/DashboardSettings";
import { WeatherSettings } from "./settings/WeatherSettings";
import { WallpaperSettings } from "./settings/WallpaperSettings";
import { InterfaceSettings } from "./settings/InterfaceSettings";
import { KioskSettings } from "./settings/KioskSettings";
import { SecuritySettings } from "./settings/SecuritySettings";
import { LanguageSettings } from "./settings/LanguageSettings";
import { Button } from "@/components/ui/Button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/utils/language";
import { useAuth } from "@/context/auth";
import {
  getCurrentThemeMode,
  setAutoTheme,
  getSystemTheme,
} from "@/utils/darkMode";
import { iconColors } from "./SettingsMenu";

const sections = [
  { id: "notifications", icon: BellIcon,                  label: "Notifications",   labelRu: "Уведомления",            component: NotificationSettings, group: "general"    },
  { id: "language",      icon: LanguageIcon,              label: "Language",         labelRu: "Язык",                   component: LanguageSettings,     group: "general"    },
  { id: "time-format",   icon: ClockIcon,                 label: "Time & Timezone",  labelRu: "Время и часовой пояс",   component: TimeFormatSettings,   group: "general"    },
  { id: "distance",      icon: MapPinIcon,                label: "Distance Units",   labelRu: "Единицы расстояния",     component: DistanceSettings,     group: "general"    },
  { id: "dashboard",     icon: PresentationChartLineIcon, label: "Dashboard",        labelRu: "Дашборд",                component: DashboardSettings,    group: "appearance" },
  { id: "weather",       icon: CloudIcon,                 label: "Weather",          labelRu: "Погода",                 component: WeatherSettings,      group: "appearance" },
  { id: "wallpaper",     icon: PhotoIcon,                 label: "Wallpaper",        labelRu: "Обои",                   component: WallpaperSettings,    group: "appearance" },
  { id: "interface",     icon: MagnifyingGlassPlusIcon,   label: "Interface scale",  labelRu: "Масштаб интерфейса",     component: InterfaceSettings,    group: "appearance" },
  { id: "kiosk",         icon: LockClosedIcon,            label: "Kiosk",            labelRu: "Киоск",                  component: KioskSettings,        group: "system"     },
  { id: "security",      icon: ShieldCheckIcon,           label: "Security",         labelRu: "Безопасность",           component: SecuritySettings,     group: "system"     },
] as const;

const groups = [
  { id: "general",    label: "General",    labelRu: "Основные"    },
  { id: "appearance", label: "Appearance", labelRu: "Внешний вид" },
  { id: "system",     label: "System",     labelRu: "Система"     },
] as const;

type SectionId = typeof sections[number]["id"];

interface MobileSettingsSheetProps {
  trigger: React.ReactNode;
}

export function MobileSettingsSheet({ trigger }: MobileSettingsSheetProps) {
  const { language } = useLanguage();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<SectionId | null>(null);
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark" | "auto">(getCurrentThemeMode);

  const isRu = language === "ru";

  const handleThemeChange = (theme: "light" | "dark" | "auto") => {
    if (theme === "auto") {
      setAutoTheme();
    } else {
      localStorage.setItem("theme", theme);
      document.documentElement.classList.toggle("dark", theme === "dark");
    }
    setCurrentTheme(theme);
    window.dispatchEvent(new CustomEvent("themechange", {
      detail: { theme: theme === "auto" ? getSystemTheme() : theme, isSystemChange: false },
    }));
  };

  const handleOpen = (state: boolean) => {
    setOpen(state);
    if (!state) setActive(null);
  };

  const section = sections.find((s) => s.id === active);
  const SectionComponent = section?.component;
  const labelFor = (s: typeof sections[number]) => isRu ? s.labelRu : s.label;
  const groupLabel = (g: typeof groups[number]) => isRu ? g.labelRu : g.label;

  const themeOptions = [
    { id: "light" as const, icon: SunIcon,             label: "Light", labelRu: "Светлая" },
    { id: "dark"  as const, icon: MoonIcon,            label: "Dark",  labelRu: "Тёмная"  },
    { id: "auto"  as const, icon: ComputerDesktopIcon, label: "Auto",  labelRu: "Авто"    },
  ];

  return (
    <Sheet open={open} onOpenChange={handleOpen}>
      <SheetTrigger asChild>{trigger}</SheetTrigger>
      <SheetContent
        side="right"
        className="flex flex-col p-0 gap-0 overflow-hidden"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-4 border-b border-gray-200 dark:border-gray-800 shrink-0">
          {active ? (
            <button
              type="button"
              onClick={() => setActive(null)}
              className="flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors -ml-1 px-1 py-0.5 rounded"
              aria-label="Back"
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>{isRu ? "Настройки" : "Settings"}</span>
            </button>
          ) : (
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {isRu ? "Настройки" : "Settings"}
            </h2>
          )}
          {active && section && (
            <>
              <span className="text-gray-300 dark:text-gray-600 text-sm">/</span>
              <span className="text-base font-semibold text-gray-900 dark:text-white truncate">
                {labelFor(section)}
              </span>
            </>
          )}
        </div>

        {/* Section content */}
        {active && SectionComponent ? (
          <div className="flex-1 overflow-y-auto px-4 py-4 modal-scrollbar">
            <SectionComponent />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto modal-scrollbar">
            {/* Theme segmented control */}
            <div className="px-4 pt-4 pb-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2.5">
                {isRu ? "Тема" : "Theme"}
              </p>
              <div className="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                {themeOptions.map(({ id, icon: Icon, label, labelRu }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleThemeChange(id)}
                    className={cn(
                      "flex-1 flex flex-col items-center gap-1.5 py-3 text-[11px] font-medium transition-all",
                      currentTheme === id
                        ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm rounded-lg m-1"
                        : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    )}
                  >
                    <Icon className="w-4 h-4" />
                    {isRu ? labelRu : label}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-px bg-gray-100 dark:bg-gray-800 mx-4 mb-1" />

            {/* Grouped sections */}
            {groups.map((group, gi) => {
              const secs = sections.filter((s) => s.group === group.id);
              return (
                <div key={group.id}>
                  <div className={cn("px-4 pb-1", gi === 0 ? "pt-3" : "pt-4")}>
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      {groupLabel(group)}
                    </span>
                  </div>
                  <nav>
                    {secs.map((s) => {
                      const Icon = s.icon;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setActive(s.id)}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                        >
                          <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-xl", iconColors[s.id])}>
                            <Icon className="w-4 h-4" />
                          </span>
                          <span className="flex-1 text-left text-sm font-medium text-gray-800 dark:text-gray-200">
                            {labelFor(s)}
                          </span>
                          <ChevronRightIcon className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
                        </button>
                      );
                    })}
                  </nav>
                </div>
              );
            })}

            <div className="h-px bg-gray-100 dark:bg-gray-800 mx-4 mt-3" />

            {/* Logout */}
            <div className="py-2">
              <Button
                onClick={() => { logout(); setOpen(false); }}
                variant="ghost"
                className="w-full justify-start gap-3 h-auto px-4 py-3 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 rounded-none"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-500 dark:bg-red-900/40 dark:text-red-400">
                  <LogoutIcon className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium">{isRu ? "Выйти" : "Sign Out"}</span>
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
